/* eslint-disable react/jsx-props-no-spreading */
import { Field, type FieldConfig, type FieldProps } from 'formik';
import Input, { type InputProps } from '@common/components/controls/Input';
import StepButtons from '@common/components/controls/StepButtons';
import { type OptionalTestId } from '@common/components/controls/TestId';
import { formatLocaleNumber, parseLocaleNumber } from '@common/utils/formatting/localeNumber';

interface InputFieldProps extends InputProps, Pick<FieldConfig, 'validate'> {
  showStepButtons?: boolean;
  precision?: number;
  locale?: string;
}

export const addStepAndRoundToPrecision = (
  value: number,
  step: number,
  precision?: number,
  locale?: string,
) => {
  const multiplier = 10 ** (precision ?? 0);
  const valueInteger = Math.round(value * multiplier);
  let stepInteger = Math.round(step * multiplier);
  if (stepInteger === 0) {
    stepInteger = step < 0 ? -1 : 1;
  }
  const result = (valueInteger + stepInteger) / multiplier;
  return locale
    ? formatLocaleNumber(result, locale, precision)
    : result.toFixed(precision);
};

function InputField(props: OptionalTestId<InputFieldProps>) {
  const {
    name,
    'data-testid': testId,
    validate,
    showStepButtons,
    precision,
    value,
    locale,
    ...rest
  } = props;

  if (name && showStepButtons) {
    const { min, max, step, ...restWithoutStepConfig } = rest;
    if (
      typeof step === 'number'
      && (typeof min === 'number' || min === null)
      && (typeof max === 'number' || max === null)
    ) {
      return (
        <Field name={name} validate={validate} value={value}>
          {({ field, form, meta: { touched, error } }: FieldProps) => (
            <span className="justify-content-space-between">
              <Input
                {...restWithoutStepConfig}
                {...field}
                data-testid={testId || `${name}-input`} // NOSONAR:S6606 - Empty testId should use default
                touched={touched}
                error={error}
              />
              <StepButtons
                min={min ?? Number.MIN_SAFE_INTEGER}
                max={max ?? Number.MAX_SAFE_INTEGER}
                step={step}
                value={field.value}
                disabled={rest.disabled}
                changeValue={(num) => {
                  const fieldValue = parseFloat(
                    locale ? parseLocaleNumber(field.value, locale) : field.value,
                  );
                  if (typeof max === 'number' && fieldValue > max) {
                    form.setFieldValue(
                      field.name,
                      locale ? formatLocaleNumber(max, locale, precision) : max,
                    );
                  } else if (typeof min === 'number' && fieldValue < min) {
                    form.setFieldValue(
                      field.name,
                      locale ? formatLocaleNumber(min, locale, precision) : min,
                    );
                  } else {
                    form.setFieldValue(
                      field.name,
                      addStepAndRoundToPrecision(
                        Number.isNaN(fieldValue) ? 0 : fieldValue,
                        num,
                        precision,
                        locale,
                      ),
                    );
                  }
                }}
              />
            </span>
          )}
        </Field>
      );
    }
  }

  return (
    <Field name={name} validate={validate} value={value}>
      {({ field, meta: { touched, error } }: FieldProps) => (
        <Input
          {...rest}
          {...field}
          data-testid={testId || `${name}-input`} // NOSONAR:S6606 - Empty testId should use default
          onChange={(event) => {
            rest.onChange?.(event);
            field.onChange?.(event);
          }}
          touched={touched}
          error={error}
        />
      )}
    </Field>
  );
}

export default InputField;
